import { supabase } from '../lib/supabase';

export const realtimeSyncService = {
  activeSubscriptions: new Map(),

  /**
   * Subscribe to scouting logs real-time updates
   */
  subscribeToScoutingLogs(companyId, callback) {
    const channel = supabase
      ?.channel(`scouting-logs-${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scouting_logs',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformScoutingLog(payload?.new) : null,
            old: payload?.old ? this.transformScoutingLog(payload?.old) : null,
            timestamp: new Date()?.toISOString()
          };
          callback(transformedPayload);
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`scouting-logs-${companyId}`, channel);
    return channel;
  },

  /**
   * Subscribe to inventory transactions real-time updates
   */
  subscribeToInventoryTransactions(companyId, callback) {
    const channel = supabase
      ?.channel(`inventory-transactions-${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'inventory_transactions',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformInventoryTransaction(payload?.new) : null,
            old: payload?.old ? this.transformInventoryTransaction(payload?.old) : null,
            timestamp: new Date()?.toISOString()
          };
          callback(transformedPayload);
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`inventory-transactions-${companyId}`, channel);
    return channel;
  },

  /**
   * Subscribe to offline sync queue real-time updates
   */
  subscribeToSyncQueue(companyId, callback) {
    const channel = supabase
      ?.channel(`sync-queue-${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'offline_sync_queue',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformSyncQueue(payload?.new) : null,
            old: payload?.old ? this.transformSyncQueue(payload?.old) : null,
            timestamp: new Date()?.toISOString()
          };
          callback(transformedPayload);
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`sync-queue-${companyId}`, channel);
    return channel;
  },

  /**
   * Subscribe to push notification devices (field team activity)
   */
  subscribeToDeviceActivity(companyId, callback) {
    const channel = supabase
      ?.channel(`device-activity-${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'push_notification_devices',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? this.transformDevice(payload?.new) : null,
            old: payload?.old ? this.transformDevice(payload?.old) : null,
            timestamp: new Date()?.toISOString()
          };
          callback(transformedPayload);
        }
      )
      ?.subscribe();

    this.activeSubscriptions?.set(`device-activity-${companyId}`, channel);
    return channel;
  },

  /**
   * Get sync statistics
   */
  async getSyncStatistics(companyId) {
    try {
      const { data: syncQueue, error: queueError } = await supabase
        ?.from('offline_sync_queue')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (queueError) throw queueError;

      const pending = syncQueue?.filter(s => s?.sync_status === 'pending')?.length || 0;
      const inProgress = syncQueue?.filter(s => s?.sync_status === 'in_progress')?.length || 0;
      const completed = syncQueue?.filter(s => s?.sync_status === 'completed')?.length || 0;
      const failed = syncQueue?.filter(s => s?.sync_status === 'failed')?.length || 0;
      const conflicts = syncQueue?.filter(s => s?.conflict_detected)?.length || 0;

      return {
        pending,
        inProgress,
        completed,
        failed,
        conflicts,
        total: syncQueue?.length || 0
      };
    } catch (error) {
      console.error('Error fetching sync statistics:', error);
      throw error;
    }
  },

  /**
   * Get active field team members
   */
  async getActiveFieldTeam(companyId) {
    try {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000)?.toISOString();
      
      const { data: devices, error } = await supabase
        ?.from('push_notification_devices')
        ?.select(`
          *,
          user_profiles!inner(
            id,
            full_name,
            email,
            role
          )
        `)
        ?.eq('company_id', companyId)
        ?.eq('is_active', true)
        ?.gte('last_active_at', fiveMinutesAgo);

      if (error) throw error;

      return devices?.map(device => ({
        userId: device?.user_id,
        userName: device?.user_profiles?.full_name,
        email: device?.user_profiles?.email,
        role: device?.user_profiles?.role,
        devicePlatform: device?.device_platform,
        lastActiveAt: device?.last_active_at,
        deviceName: device?.device_name
      })) || [];
    } catch (error) {
      console.error('Error fetching active field team:', error);
      throw error;
    }
  },

  /**
   * Get recent sync operations
   */
  async getRecentSyncOperations(companyId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('offline_sync_history')
        ?.select(`
          *,
          user_profiles(
            full_name,
            email
          )
        `)
        ?.order('synced_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(sync => ({
        id: sync?.id,
        tableName: sync?.table_name,
        operationType: sync?.operation_type,
        syncStatus: sync?.sync_status,
        recordsAffected: sync?.records_affected,
        conflictsResolved: sync?.conflicts_resolved,
        syncDurationMs: sync?.sync_duration_ms,
        syncedAt: sync?.synced_at,
        userName: sync?.user_profiles?.full_name,
        deviceId: sync?.device_id,
        networkType: sync?.network_type
      })) || [];
    } catch (error) {
      console.error('Error fetching recent sync operations:', error);
      throw error;
    }
  },

  /**
   * Get pending sync queue items
   */
  async getPendingSyncQueue(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('offline_sync_queue')
        ?.select(`
          *,
          user_profiles(
            full_name,
            email
          )
        `)
        ?.eq('company_id', companyId)
        ?.in('sync_status', ['pending', 'in_progress'])
        ?.order('priority', { ascending: false })
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(item => ({
        id: item?.id,
        tableName: item?.table_name,
        operationType: item?.operation_type,
        syncStatus: item?.sync_status,
        priority: item?.priority,
        retryCount: item?.retry_count,
        conflictDetected: item?.conflict_detected,
        createdAt: item?.created_at,
        userName: item?.user_profiles?.full_name,
        deviceId: item?.device_id
      })) || [];
    } catch (error) {
      console.error('Error fetching pending sync queue:', error);
      throw error;
    }
  },

  /**
   * Get data validation issues
   */
  async getDataValidationIssues(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('offline_sync_queue')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.or('conflict_detected.eq.true,sync_status.eq.failed')
        ?.order('created_at', { ascending: false })
        ?.limit(20);

      if (error) throw error;

      return data?.map(item => ({
        id: item?.id,
        tableName: item?.table_name,
        operationType: item?.operation_type,
        conflictDetected: item?.conflict_detected,
        conflictResolutionStrategy: item?.conflict_resolution_strategy,
        lastError: item?.last_error,
        retryCount: item?.retry_count,
        createdAt: item?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching validation issues:', error);
      throw error;
    }
  },

  /**
   * Resolve sync conflict
   */
  async resolveSyncConflict(syncQueueId, resolution) {
    try {
      const { data, error } = await supabase
        ?.from('offline_sync_queue')
        ?.update({
          conflict_detected: false,
          conflict_resolution_strategy: resolution,
          sync_status: 'pending',
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', syncQueueId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error resolving sync conflict:', error);
      throw error;
    }
  },

  /**
   * Transform functions for camelCase conversion
   */
  transformScoutingLog(log) {
    return {
      id: log?.id,
      companyId: log?.company_id,
      userId: log?.user_id,
      scoutingDate: log?.scouting_date,
      room: log?.room,
      cultivar: log?.cultivar,
      isContaminated: log?.is_contaminated,
      comments: log?.comments,
      createdAt: log?.created_at
    };
  },

  transformInventoryTransaction(transaction) {
    return {
      id: transaction?.id,
      inventoryItemId: transaction?.inventory_item_id,
      companyId: transaction?.company_id,
      transactionType: transaction?.transaction_type,
      quantity: transaction?.quantity,
      unitType: transaction?.unit_type,
      createdAt: transaction?.created_at
    };
  },

  transformSyncQueue(queue) {
    return {
      id: queue?.id,
      tableName: queue?.table_name,
      operationType: queue?.operation_type,
      syncStatus: queue?.sync_status,
      conflictDetected: queue?.conflict_detected,
      priority: queue?.priority,
      retryCount: queue?.retry_count,
      createdAt: queue?.created_at
    };
  },

  transformDevice(device) {
    return {
      id: device?.id,
      userId: device?.user_id,
      devicePlatform: device?.device_platform,
      isActive: device?.is_active,
      lastActiveAt: device?.last_active_at
    };
  },

  /**
   * Unsubscribe from a specific channel
   */
  unsubscribe(channelKey) {
    const channel = this.activeSubscriptions?.get(channelKey);
    if (channel) {
      supabase?.removeChannel(channel);
      this.activeSubscriptions?.delete(channelKey);
    }
  },

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll() {
    this.activeSubscriptions?.forEach((channel) => {
      supabase?.removeChannel(channel);
    });
    this.activeSubscriptions?.clear();
  }
};